import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faBuildingUser,
  faChartKanban,
  faChartPie,
  faCirclePlus,
  faCrosshairsSimple,
  faEnvelope,
  faFileInvoice,
  faFileUpload,
  faGaugeHigh,
  faIndustry,
  faMoneyCheckDollar,
  faPaperPlaneTop,
  faShuffle,
  faUser,
  faUserGraduate
} from '@fortawesome/pro-light-svg-icons'

export interface MenuItem {
  id: string
  url?: string
  v3?: boolean
  isBeta?: boolean
  label: string
  icon?: IconDefinition
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

export type MenuItemPrimary = MenuItem & {
  submenu?: MenuItem[]
}

export interface AclsStructure {
  dashboard: boolean
  applicants: {
    create: boolean
    search: boolean
    import: boolean
    sms: boolean
  }
  orders: {
    create: boolean
  }
  reporting: boolean
}

export const getMenu = (
  acls?: AclsStructure,
  isKanbanDefault?: boolean,
  invoiceEnabled?: boolean,
  ordersV3Only?: boolean,
  isApplicationsV3Default?: boolean
): MenuItemPrimary[] => {
  const userAcls = acls ?? {
    dashboard: true,
    applicants: {
      create: true,
      search: true,
      import: true,
      sms: true
    },
    orders: { create: true },
    reporting: false
  }
  return [
    {
      id: 'dashboard',
      url: '/dashboard',
      label: 'Dashboard',
      icon: faGaugeHigh,
      v3: true
    },
    ...(userAcls.applicants.create ||
    userAcls.applicants.search ||
    userAcls.applicants.import ||
    userAcls.applicants.sms
      ? [
          {
            id: 'applicants',
            label: 'Candidats',
            url: userAcls.applicants.search
              ? '/admin/candidats/search'
              : undefined,
            icon: faUser
          }
        ]
      : []),
    {
      id: 'companies',
      label: 'Entreprises',
      url: '/admin/contacts/search',
      icon: faIndustry,
      submenu: [
        {
          id: 'contacts_search',
          label: 'Contacts',
          url: '/admin/contacts/search',
          icon: faBuildingUser
        },
        {
          id: 'companies_search',
          label: 'Entreprises',
          url: '/admin/entreprises/search',
          icon: faIndustry
        },
        {
          id: 'companies_v3',
          label: 'Entreprises',
          url: '/companies',
          icon: faIndustry,
          v3: true,
          isBeta: true
        }
      ]
    },
    {
      id: 'orders',
      label: 'Commandes',
      url:
        ordersV3Only || isKanbanDefault
          ? '/orders'
          : '/admin/recherches/commande',
      icon: faFileInvoice,
      submenu: ordersV3Only
        ? undefined
        : [
            {
              id: 'orders_search',
              label: 'Liste des commandes',
              url: '/admin/recherches/commande',
              icon: faFileInvoice
            },
            {
              id: 'orders_kanban',
              label: 'Vue Kanban',
              url: '/orders',
              icon: faChartKanban,
              v3: true
            }
          ],
      v3: ordersV3Only || isKanbanDefault
    },
    ...(invoiceEnabled
      ? [
          {
            id: 'finance',
            label: 'Facturation',
            url: '/finance',
            icon: faMoneyCheckDollar,
            v3: true
          }
        ]
      : []),
    {
      id: 'offers',
      label: "Offres d'emploi",
      url: '/admin/offres/search',
      icon: faShuffle
    },
    {
      id: 'jobmail',
      url: isApplicationsV3Default ? '/applications' : '/admin/jobmail',
      label: 'Candidatures',
      icon: faUserGraduate,
      submenu: [
        {
          id: 'jobmail_legacy',
          url: '/admin/jobmail',
          icon: faEnvelope,
          label: 'Jobmail'
        },
        {
          id: 'jobmail_v3',
          url: '/applications',
          icon: faUserGraduate,
          label: 'Candidatures',
          v3: true,
          isBeta: true
        }
      ],
      v3: isApplicationsV3Default
    },
    {
      id: 'push',
      label: 'Placement actif',
      url: '/admin/push',
      icon: faPaperPlaneTop
    },
    {
      id: 'reporting',
      url: '/admin/statistiques/reporting',
      label: 'Reporting',
      icon: faChartPie
    },
    {
      id: 'create',
      label: 'Créer',
      url: '/admin/candidats/create',
      submenu: [
        ...(userAcls.applicants.create
          ? [
              {
                id: 'candidat_import',
                label: 'Import de CV',
                url: '/admin/candidats/upload',
                icon: faFileUpload
              },
              {
                id: 'candidat',
                url: '/admin/candidats/create',
                label: 'Candidat',
                icon: faUser
              }
            ]
          : []),
        {
          id: 'entreprise',
          url: '/admin/entreprises/create',
          label: 'Entreprise',
          icon: faIndustry
        },
        ...(userAcls.orders.create
          ? [
              {
                id: 'create_order',
                label: 'Commande',
                icon: faFileInvoice
              },
              {
                id: 'recours',
                url: '/admin/recours/create',
                label: 'Recours',
                icon: faCrosshairsSimple
              }
            ]
          : []),
        {
          id: 'offre',
          url: '/admin/offres/saisie_offres',
          label: "Offre d'emploi",
          icon: faShuffle
        },
        {
          id: 'push_offre',
          url: '/admin/push/offres',
          label: 'Push Offre',
          icon: faPaperPlaneTop
        }
      ],
      icon: faCirclePlus
    }
  ]
}
